import { clamp, debounce } from 'lodash-es';
import { create } from 'zustand';

import type { ToolStoreActions, ToolStoreState } from '@/type';

import useToolPermissionStore from '../toolPermissionStore';
import { TOOL_MENU_TYPE } from './constant';
import type { ToolMenuItem } from './type';
import {
  checkToolPermission,
  createInitBoxGenToolList,
  createToolMap,
  createToolMenuList,
  deselectTool,
  findSelectedToolList,
  selectChildTool,
  selectTool,
  setDisabledTool,
} from './util';

const useToolMenuStore = create<
  ToolStoreState<ToolMenuItem> & ToolStoreActions<ToolMenuItem>
>((set, get) => {
  const initialToolList = createToolMenuList();
  const initialToolMap = createToolMap(initialToolList);

  const getPermission = () => useToolPermissionStore.getState().permission;
  const getToolList = () => [...get().toolList];
  const setToolList = (toolList: ToolMenuItem[]) => {
    const toolMap = createToolMap(toolList);

    set({
      toolList,
      toolMap,
      toolMenuIdList: Object.keys(toolMap),
    });
  };

  return {
    isToolListInitialized: false,
    toolList: initialToolList,
    toolMenuIdList: Object.keys(initialToolMap),
    toolMap: initialToolMap,

    clear() {
      get().deselectAll(['editing', 'leftPopup', 'screenView']);

      const viewModeTool = get().findByName('viewMode');

      if (viewModeTool) {
        get().selectChildren(viewModeTool.id, viewModeTool.children[0].id);
      }
    },

    checkPermission(toolMenuOrToolId) {
      const toolMenu =
        typeof toolMenuOrToolId === 'string'
          ? get().findBy('id', toolMenuOrToolId)
          : toolMenuOrToolId;

      if (!toolMenu) return false;

      return checkToolPermission(toolMenu, getPermission());
    },

    deselect(toolId) {
      const toolList = getToolList();
      deselectTool(toolList, toolId);

      setToolList(toolList);
    },

    deselectAll(toolGroupList) {
      const toolList = getToolList();

      toolList.forEach(tool => {
        if (!toolGroupList?.length || toolGroupList.includes(tool.group)) {
          deselectTool(toolList, tool.id);
        }
      });

      setToolList(toolList);
    },

    deselectByCommand(command) {
      const tool = get().findByCommand(command);

      if (!tool) return;

      get().deselect(tool.id);
    },

    deselectByName(name) {
      const tool = get().findByName(name);

      if (!tool) return;

      get().deselect(tool.id);
    },

    findBy(key, value) {
      const { toolMap, toolMenuIdList, toolList } = get();
      const id = toolMenuIdList.find(id => {
        const tool = toolMap[id];

        return tool[key] === value;
      });

      if (id) return toolMap[id];

      const findInChildren = (items: ToolMenuItem[]): ToolMenuItem | null => {
        for (const item of items) {
          if (item[key] === value) {
            return item;
          }
          if (item.children.length > 0) {
            const found = findInChildren(item.children);
            if (found) return found;
          }
        }
        return null;
      };

      return findInChildren(toolList);
    },

    findByCommand(command) {
      return get().findBy('command', command);
    },

    findByName(toolName) {
      return get().findBy('name', toolName);
    },

    getBoxToolList: () => {
      const boxTool = get().toolList.find(tool => tool.name === 'box');
      return boxTool ? boxTool.children : [];
    },

    getSelectedToolMenuList: () => {
      return findSelectedToolList(get().toolList);
    },

    initBoxToolList(superObjectClassList) {
      const toolList = getToolList();
      const boxTool = toolList.find(tool => tool.name === 'box');

      if (!boxTool) return;

      const initBoxToolList = createInitBoxGenToolList(superObjectClassList);

      boxTool.children = initBoxToolList;
      boxTool.children.length > 0 && (boxTool.children[0].isSelected = true);

      setToolList(toolList);
      set({ isToolListInitialized: true });
    },

    isSelected(toolOrToolID) {
      const toolID =
        typeof toolOrToolID === 'string' ? toolOrToolID : toolOrToolID.id;

      return get()
        .getSelectedToolMenuList()
        .some(m => m.id === toolID);
    },

    isSelectedByToolName(name) {
      const tool = get().findBy('name', name);

      return !tool ? false : get().isSelected(tool);
    },

    select(toolId) {
      const tool = get().toolMap[toolId];

      if (
        !tool ||
        (get().isSelected(tool) &&
          tool.type !== TOOL_MENU_TYPE.TOGGLE &&
          tool.type !== TOOL_MENU_TYPE.SWITCH) ||
        !checkToolPermission(tool, getPermission())
      )
        return;

      const toolList = getToolList();

      switch (tool.type) {
        case TOOL_MENU_TYPE.TOGGLE:
          if (tool.isSelected) {
            deselectTool(toolList, toolId);
          } else {
            selectTool(toolList, toolId);
          }
          break;

        case TOOL_MENU_TYPE.IMMEDIATELY:
          selectTool(toolList, toolId);
          debounce(get().deselect, 5)(toolId);
          break;

        case TOOL_MENU_TYPE.SWITCH: {
          const childrenLength = tool.children.length;
          const selectedChildIndex = tool.children.findIndex(c => c.isSelected);
          const nextChildIndex = clamp(
            selectedChildIndex === childrenLength - 1
              ? 0
              : selectedChildIndex + (tool.isSelected ? 1 : 0),
            0,
            childrenLength - 1,
          );

          get().selectChildren(toolId, tool.children[nextChildIndex].id);
          break;
        }
        case TOOL_MENU_TYPE.DEFAULT:
        default:
          selectTool(toolList, toolId);
          break;
      }

      setToolList([...toolList]);
    },

    selectByCommand(command) {
      const tool = get().findByCommand(command);

      if (!tool) return;

      get().select(tool.id);
    },

    selectByName(name) {
      const tool = get().findBy('name', name);

      if (!tool) return;

      get().select(tool.id);
    },

    selectChildren(parentToolId, childToolId) {
      const parentTool = get().toolMap[parentToolId];

      if (!parentTool || !checkToolPermission(parentTool, getPermission()))
        return;

      const toolList = getToolList();

      selectTool(toolList, parentToolId);

      selectChildTool(toolList, parentToolId, childToolId);

      setToolList(toolList);
    },

    setAllDisabled(disabled) {
      const toolList = getToolList();

      toolList.forEach(tool => setDisabledTool(toolList, tool.id, disabled));

      setToolList(toolList);
    },

    setDisabled(toolId, disabled) {
      const toolList = getToolList();

      setDisabledTool(toolList, toolId, disabled);

      setToolList(toolList);
    },

    setIsToolListInitialized(isToolListInitialized) {
      set({ isToolListInitialized });
    },
  };
});

export default useToolMenuStore;
